import { useQuery } from '@tanstack/react-query';
import { sanityClient } from '../lib/sanity';

export interface HomepageSettings {
  hero?: {
    headline?: string;
    subheadline?: string;
    backgroundImage?: {
      asset: {
        url: string;
      };
      alt?: string;
    };
    ctaPrimaryText?: string;
    ctaPrimaryLink?: string;
    ctaSecondaryText?: string;
    ctaSecondaryLink?: string;
  };
  trustBadges?: {
    enabled?: boolean;
    badges?: Array<{
      icon: string;
      number: string;
      label: string;
    }>;
  };
  whyChooseUs?: {
    enabled?: boolean;
    heading?: string;
    subheading?: string;
    features?: Array<{
      _id: string;
      title: string;
      description: string;
      icon: string;
      order?: number;
    }>;
  };
  testimonials?: {
    enabled?: boolean;
    heading?: string;
    subheading?: string;
    items?: Array<{
      _id: string;
      clientName: string;
      clientRole?: string;
      quote: string;
      rating: number;
      clientPhoto?: {
        asset: {
          url: string;
        };
      };
      featured?: boolean;
    }>;
  };
  servicesPreview?: {
    enabled?: boolean;
    heading?: string;
    subheading?: string;
    homeWatch?: {
      title: string;
      description: string;
      image?: {
        asset: {
          url: string;
        };
        alt?: string;
      };
      link: string;
    };
    concierge?: {
      title: string;
      description: string;
      image?: {
        asset: {
          url: string;
        };
        alt?: string;
      };
      link: string;
    };
  };
  ctaSection?: {
    enabled?: boolean;
    heading?: string;
    subheading?: string;
    backgroundImage?: {
      asset: {
        url: string;
      };
      alt?: string;
    };
    buttonText?: string;
    buttonLink?: string;
    trustIndicators?: Array<{
      icon: string;
      text: string;
    }>;
  };
}

/**
 * Hook to fetch homepage settings from Sanity CMS
 * Falls back to hardcoded defaults if no data is available
 */
export const useHomepageSettings = () => {
  const dataset = import.meta.env.VITE_SANITY_DATASET || 'production';
  // Use shorter cache for non-production to see changes faster
  const staleTime = dataset === 'production' ? 1000 * 60 : 1000 * 10; // 1min prod, 10sec staging

  return useQuery<HomepageSettings>({
    queryKey: ['homepageSettings'],
    queryFn: async () => {
      const query = `*[_type == "homepageSettings" && _id == "homepage"][0] {
        hero {
          headline,
          subheadline,
          backgroundImage {
            asset->{ url },
            alt
          },
          ctaPrimaryText,
          ctaPrimaryLink,
          ctaSecondaryText,
          ctaSecondaryLink
        },
        trustBadges {
          enabled,
          badges[] {
            icon,
            number,
            label
          }
        },
        whyChooseUs {
          enabled,
          heading,
          subheading,
          features[]-> {
            _id,
            title,
            description,
            icon,
            order
          }
        },
        testimonials {
          enabled,
          heading,
          subheading,
          items[]-> {
            _id,
            clientName,
            clientRole,
            quote,
            rating,
            clientPhoto {
              asset->{ url }
            },
            featured
          }
        },
        servicesPreview {
          enabled,
          heading,
          subheading,
          homeWatch {
            title,
            description,
            image {
              asset->{ url },
              alt
            },
            link
          },
          concierge {
            title,
            description,
            image {
              asset->{ url },
              alt
            },
            link
          }
        },
        ctaSection {
          enabled,
          heading,
          subheading,
          backgroundImage {
            asset->{ url },
            alt
          },
          buttonText,
          buttonLink,
          trustIndicators[] {
            icon,
            text
          }
        }
      }`;

      const data = await sanityClient.fetch<HomepageSettings | null>(query);
      return data || {};
    },
    staleTime,
    retry: 1, // Only retry once on failure
  });
};
